import React from 'react';
import { Row, Col, Card, Typography } from 'antd';
import {
  RocketOutlined,
  BulbOutlined,
  TrophyOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';

const { Title } = Typography;

/**
 * Concise, evergreen "how to get your first responses" guide — the roadmap +
 * incentive advice from the onboarding wizard's final step, minus the
 * trial-timing urgency banner and the survey-specific share card. Shared by the
 * wizard (step 3) and the marketer-view empty state so a user who clicked past
 * onboarding can always recall it. Uses the `onboarding.survey.finalStep.*`
 * i18n keys.
 */
const roadmapSteps = [
  { icon: <RocketOutlined />, key: 'roadmapStep1' },
  { icon: <BulbOutlined />, key: 'roadmapStep2' },
  { icon: <TrophyOutlined />, key: 'roadmapStep3' },
];

export const GettingStartedGuide: React.FunctionComponent = () => {
  const intl = useIntl();

  return (
    <div>
      {/* Roadmap */}
      <div style={{ marginBottom: '20px' }}>
        <Title level={5} style={{ margin: '0 0 12px 0', color: '#0F172A', fontWeight: 700 }}>
          {intl.formatMessage({ id: 'onboarding.survey.finalStep.roadmapTitle' })}
        </Title>
        <Row gutter={[12, 12]} align="stretch">
          {roadmapSteps.map((step) => (
            <Col xs={24} md={8} key={step.key} style={{ display: 'flex' }}>
              <div style={{
                padding: '16px 12px',
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: 16,
                textAlign: 'center',
                width: '100%',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: '#F59E0B',
                  color: 'white',
                  fontSize: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px',
                  boxShadow: '0 3px 8px rgba(245, 158, 11, 0.3)',
                }}>
                  {step.icon}
                </div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {intl.formatMessage({ id: `onboarding.survey.finalStep.${step.key}Number` })}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#18216d', margin: '4px 0 6px' }}>
                  {intl.formatMessage({ id: `onboarding.survey.finalStep.${step.key}Title` })}
                </div>
                <div style={{ fontSize: '12px', color: '#64748B', lineHeight: 1.4 }}>
                  {intl.formatMessage({ id: `onboarding.survey.finalStep.${step.key}Description` })}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Incentive Callout */}
      <Card
        style={{
          marginBottom: '20px',
          background: '#FEF3C7',
          border: '1px solid #E5E7EB',
          borderRadius: 16,
        }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ padding: '24px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <div style={{
            width: '46px',
            height: '46px',
            borderRadius: '50%',
            background: 'white',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #F59E0B',
          }}>
            <GiftOutlined style={{ fontSize: '22px', color: '#F59E0B' }} />
          </div>
          <div style={{ flex: 1 }}>
            <Title level={5} style={{ margin: '0 0 6px 0', color: '#18216d', fontWeight: 700 }}>
              {intl.formatMessage({ id: 'onboarding.survey.finalStep.incentiveTitle' })}
            </Title>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#18216d', marginBottom: '6px', lineHeight: 1.4 }}>
              {intl.formatMessage({ id: 'onboarding.survey.finalStep.incentiveRecommendation' })}
            </div>
            <div style={{ fontSize: '12px', color: '#64748B', lineHeight: 1.5, marginBottom: '12px' }}>
              {intl.formatMessage({ id: 'onboarding.survey.finalStep.incentiveRationale' })}
            </div>
            <div style={{
              paddingTop: '12px',
              borderTop: '1px dashed rgba(245, 158, 11, 0.4)',
            }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#18216d', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '8px' }}>
                {intl.formatMessage({ id: 'onboarding.survey.finalStep.incentiveDeliveryTitle' })}
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#0F172A', lineHeight: 1.5 }}>
                <li style={{ marginBottom: '4px' }}>
                  {intl.formatMessage({ id: 'onboarding.survey.finalStep.incentiveDeliveryOption1' })}
                </li>
                <li>
                  {intl.formatMessage({ id: 'onboarding.survey.finalStep.incentiveDeliveryOption2' })}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
